// React
import { useEffect, useState } from 'react';

// Hooks
import { useAppTheme } from "../../hooks/useAppTheme.js";

// Components 
import { Avatar, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, Modal, Paper, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { DeleteOutlined, CloseOutlined, PersonOutline, EmailOutlined, PhoneOutlined, CakeOutlined, GroupsOutlined, EmojiEventsOutlined, FeedbackOutlined } from '@mui/icons-material';
import CustomFormControl from '../custom/FormControl.js';
import { CustomSelect, CustomMenuItem, getMenuProps } from '../custom/Select.js';
import SearchableAthleteSelector from '../custom/SearchableAthleteSelector.js';
import CustomSnackbar from '../custom/CustomSnackbar.js';

// Style
import { green, grey, red } from '@mui/material/colors/index.js';
import { darkTheme } from "../../styles/colorSchemes/index.js";

// Other
import { capitalizeString, formatDate3 } from '../../utils/index.js';
import { getAthletesOfCoach, getAthletesOfParent } from '../../service/httpService/athlete/index.js';
import { getCoachesOfAthlete, updateAthletesOfCoach } from '../../service/httpService/coach/index.js';
import { getParentsOfAthlete, updateAthletesOfParent } from '../../service/httpService/parent/index.js';
import { updateSquad } from '../../service/httpService/squad/index.js';
import { deleteProfile as delProfile, updateProfile } from '../../service/httpService/profile/index.js';
import { updateHasExtendedFeedback } from '../../service/httpService/feedback/index.js';
import { sendPasswordLink } from '../../service/httpService/passwordLink/index.js';


const EditUser = (props) => {
    const { isDarkMode } = useAppTheme();

    const { athletes, color, open, onClose, person, profileType } = props;

    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [openFail, setOpenFail] = useState(false);
    const [failMessage, setFailMessage] = useState('');
    const [openSuccess, setOpenSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [firstName, setFirstName] = useState(person.profile.firstName);
    const [lastName, setLastName] = useState(person.profile.lastName);
    const [dateOfBirth, setDateOfBirth] = useState(formatDate3(person.profile.dateOfBirth));
    const [email, setEmail] = useState(person.profile.email);
    const [telephone, setTelephone] = useState(person.profile.telephone);
    const [hasExtendedFeedback, setHasExtendedFeedback] = useState(profileType === 'athlete' ? person.hasExtendedFeedback : undefined);
    const [squad, setSquad] = useState(profileType === 'athlete' ? person.squad : undefined);
    const [selectedAthletes, setSelectedAthletes] = useState([]);
    const [selectedCoaches, setSelectedCoaches] = useState([]);
    const [selectedParents, setSelectedParents] = useState([]);
    const [menu, setMenu] = useState([]);
    const [link, setLink] = useState('');
    const [openLink, setOpenLink] = useState(false);
    const [squadSelectAnchorEl, setSquadSelectAnchorEl] = useState(null);
    const [loadingAthletes, setLoadingAthletes] = useState(profileType === 'coach' || profileType === 'parent');
    const [isadmin, setIsadmin] = useState(person.profile.isadmin || false)

    useEffect(() => {
        if (!open) return;

        setFirstName(person.profile.firstName);
        setLastName(person.profile.lastName);
        setDateOfBirth(formatDate3(person.profile.dateOfBirth));
        setEmail(person.profile.email);
        setTelephone(person.profile.telephone);
        setIsadmin(person.profile.isadmin || false);
        if (profileType === 'coach') {
            const minDelay = new Promise(resolve => setTimeout(resolve, 350));
            Promise.all([getAthletesOfCoach(person.profile.email), minDelay])
                .then(([response]) => {
                    setSelectedAthletes(response.data);
                })
                .catch(() => {
                    setOpenFail(true);
                    setFailMessage('Fehler beim Abrufen der Athleten aufgetreten!');
                })
                .finally(() => {
                    setLoadingAthletes(false);
                });
        }
        if (profileType === 'parent') {
            const minDelay = new Promise(resolve => setTimeout(resolve, 350));
            Promise.all([getAthletesOfParent(person.profile.email), minDelay])
                .then(([response]) => {
                    setSelectedAthletes(response.data);
                })
                .catch(() => {
                    setOpenFail(true);
                    setFailMessage('Fehler beim Abrufen der Athleten aufgetreten!');
                })
                .finally(() => {
                    setLoadingAthletes(false);
                });
        }
        if (profileType === 'athlete') {
            getCoachesOfAthlete(person.profile.email)
                .then(response => {
                    setSelectedCoaches(response.data);
                    getParentsOfAthlete(person.profile.email).then(response => {
                        setSelectedParents(response.data);
                    }).catch(() => {
                        setOpenFail(true);
                        setFailMessage('Fehler beim Abrufen der Eltern aufgetreten!');
                    });
                })
                .catch(() => {
                    setOpenFail(true);
                    setFailMessage('Fehler beim Abrufen der Trainer aufgetreten!');
                });
            setHasExtendedFeedback(person.hasExtendedFeedback);
            setSquad(person.squad);
        }
    }, [person, profileType, open]);

    const onChangeSelectedAthletes = (event) => {
        setSelectedAthletes(event.target.value);
    }

    const onChangeFirstName = (event) => {
        if (!/\d/.test(event.target.value)) {
            setFirstName(capitalizeString(event.target.value));
        }
    }

    const onChangeLastName = (event) => {
        if (!/\d/.test(event.target.value)) {
            setLastName(capitalizeString(event.target.value));
        }
    }

    const onChangeDateOfBirth = (event) => {
        if (event.target.value.length <= 10) {
            setDateOfBirth(event.target.value);
        }
    }

    // Accent color should always match the avatar/athlete color (same behavior as light mode)
    const accentColor = color;

    // Dark mode field styling helper
    const getFieldSx = (isDisabled = false) => ({
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: isDarkMode
                ? (isDisabled ? 'rgba(255, 255, 255, 0.05)' : darkTheme.headerBg)
                : (isDisabled ? 'rgba(0, 0, 0, 0.02)' : undefined),
            '& fieldset': {
                borderColor: isDarkMode ? darkTheme.cardBorder : undefined,
            },
            '&:hover fieldset': {
                borderColor: isDisabled ? undefined : accentColor,
            },
            '&.Mui-focused fieldset': {
                borderColor: accentColor,
            }
        },
        '& .MuiInputLabel-root': {
            color: isDarkMode ? darkTheme.textSecondary : undefined,
            '&.Mui-focused': {
                color: accentColor,
            }
        },
        '& .MuiInputBase-input': {
            color: isDarkMode ? darkTheme.textPrimary : undefined,
            WebkitTextFillColor: isDarkMode && isDisabled ? darkTheme.textSecondary : undefined,
            opacity: isDarkMode && isDisabled ? 1 : undefined,
            '&::-webkit-calendar-picker-indicator': {
                filter: isDarkMode ? 'invert(1)' : undefined,
            }
        }
    });

    const handleClickForgotPassword = () => {
        sendPasswordLink(email)
            .then(response => {
                /*const baseUrl = process.env.NODE_ENV === 'production' 
                ? 'https://s-i-mon.de' 
                : 'http://localhost:3001';*/
                setLink(`Die E-Mail zum Zurücksetzen des Passworts wurde erfolgreich versendet.`);
                setOpenLink(true);
            })
            .catch(() => {
                setFailMessage('Erstellen des Links fehlgeschlagen!');
                setOpenFail(true);
            });
    };

    const editProfile = () => {
        if (lastName.trim() === '') {
            setFailMessage('Nachname darf nicht leer sein.');
            setOpenFail(true);
            return;
        }
        const profile = { dateOfBirth, email, firstName, lastName, telephone }

        if (profileType === 'coach') {
            profile.isadmin = isadmin;
        }

        if (profileType === 'athlete') {
            updateSquad(email, { squad: squad })
                .then(() => {
                    updateHasExtendedFeedback(email, { hasExtendedFeedback: hasExtendedFeedback })
                        .then(() => {
                            updateProfile(person.profile.email, profile)
                                .then(() => {
                                    setOpenSuccess(true);
                                    setTimeout(() => {
                                        setOpenSuccess(false);
                                        onClose();
                                        if (props.onSuccess) props.onSuccess();
                                    }, 1200);
                                })
                                .catch(() => {
                                    setOpenFail(true);
                                    setFailMessage('Aktualisieren des Profils fehlgeschlagen!');
                                });
                        })
                        .catch(() => {
                            setOpenFail(true);
                            setFailMessage('Aktualisieren der erweiterten Rückmeldung fehlgeschlagen!');
                        });
                })
                .catch(() => {
                    setOpenFail(true);
                    setFailMessage('Aktualisieren des Kaderstatus fehlgeschlagen!');
                });
        }

        if (profileType === 'coach' || profileType === 'parent') {
            let emails = [];
            for (let athlete of selectedAthletes) {
                emails.push(athlete.profile.email);
            }

            updateProfile(person.profile.email, profile)
                .then(() => {
                    if (profileType === 'coach') {
                        updateAthletesOfCoach(person.profile.email, { athletes: emails })
                            .then(() => {
                                setSuccessMessage('Profil erfolgreich aktualisiert!');
                                setOpenSuccess(true);
                                setTimeout(() => {
                                    setOpenSuccess(false);
                                    onClose();
                                    if (props.onSuccess) props.onSuccess();
                                }, 1200);
                            })
                            .catch(() => {
                                setOpenFail(true);
                                setFailMessage('Aktualisieren des Trainers fehlgeschlagen!');
                            });
                    } else {
                        updateAthletesOfParent(person.profile.email, { athletes: emails })
                            .then(() => {
                                setSuccessMessage('Profil erfolgreich aktualisiert!');
                                setOpenSuccess(true);
                                setTimeout(() => {
                                    setOpenSuccess(false);
                                    onClose();
                                    if (props.onSuccess) props.onSuccess();
                                }, 1200);
                            })
                            .catch(() => {
                                setOpenFail(true);
                                setFailMessage('Aktualisieren des Elternteils fehlgeschlagen!');
                            });
                    }
                })
                .catch(() => {
                    setOpenFail(true);
                    setFailMessage('Aktualisieren des Profils fehlgeschlagen!');
                });
        }
    }

    useEffect(() => {
        let values = [];
        for (let b of selectedAthletes) {
            values.push(b);
        }
        for (let a of athletes) {
            if (values.filter(v => v.profile.email === a.profile.email).length === 0) {
                values.push(a);
            }
        }
        setMenu(values);
    }, [athletes, selectedAthletes]);

    const deleteProfile = () => {
        delProfile(person.profile.email)
            .then(() => {
                setOpenConfirmation(false);
                setSuccessMessage('Profil erfolgreich gelöscht!');
                setOpenSuccess(true);
                setTimeout(() => {
                    setOpenSuccess(false);
                    onClose();
                    if (props.onSuccess) props.onSuccess();
                }, 1200);
            })
            .catch(() => {
                setFailMessage('Löschen des Profils fehlgeschlagen!');
                setOpenFail(true);
            });
    }

    const convertBufferToBase64String = () => {
        return btoa(String.fromCharCode(...new Uint8Array(person.profile.picture.data)));
    }

    return (
        <>
            <CustomSnackbar
                open={openFail}
                onClose={() => setOpenFail(false)}
                message={failMessage}
                severity="error"
                autoHideDuration={4000}
            />

            <CustomSnackbar
                open={openSuccess}
                onClose={() => setOpenSuccess(false)}
                message={successMessage}
                severity="success"
                autoHideDuration={3000}
            />

            <CustomSnackbar
                open={openLink}
                onClose={() => setOpenLink(false)}
                message={link}
                severity="success"
                showCopyAction={true}
            />

            <Modal
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    '@media (max-width: 900px)': {
                        alignItems: 'flex-start',
                        p: 0,
                        overflow: 'auto'
                    }
                }}
                open={open}
                onClose={onClose}
            >
                <Paper
                    sx={{
                        width: '90%',
                        maxWidth: '1200px',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        borderRadius: 3,
                        boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        bgcolor: isDarkMode ? darkTheme.accordionBg : undefined,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        position: 'relative',
                        transform: {
                            xs: 'scale(1)',
                            sm: 'scale(1)',
                            md: 'scale(1)'
                        },
                        '@media (max-height: 800px)': {
                            transform: 'scale(0.9)',
                            maxHeight: '95vh'
                        },
                        '@media (max-height: 700px)': {
                            transform: 'scale(0.8)',
                            maxHeight: '98vh'
                        },
                        '@media (max-height: 600px)': {
                            transform: 'scale(0.7)',
                            maxHeight: '98vh'
                        },
                        '@media (max-width: 900px)': {
                            width: '100%',
                            minHeight: '100vh',
                            maxHeight: '100vh',
                            borderRadius: 0,
                            m: 0,
                            transform: 'scale(1)'
                        }
                    }}
                >
                    {/* Close Button */}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            zIndex: 10,
                            bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(255, 255, 255, 0.9)',
                            color: isDarkMode ? darkTheme.textSecondary : undefined,
                            '&:hover': {
                                bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.05)',
                                transform: 'rotate(90deg)',
                                transition: 'transform 0.3s ease'
                            }
                        }}
                    >
                        <CloseOutlined />
                    </IconButton>

                    {/* Left Side - Profile Info */}
                    <Box
                        sx={{
                            width: { xs: '100%', md: '35%' },
                            background: `linear-gradient(135deg, ${color}22 0%, ${color}55 100%)`,
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3,
                            minHeight: { xs: '250px', md: 'auto' },
                            '@media (max-width: 900px)': {
                                p: 3,
                                gap: 2,
                                minHeight: 'auto',
                                flexShrink: 0,
                                alignItems: 'center',
                                textAlign: 'center'
                            }
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 180,
                                height: 180,
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                bgcolor: color,
                                color: grey[100],
                                fontSize: '3.5rem',
                                '@media (max-width: 900px)': {
                                    mr: 5,
                                    width: 120,
                                    height: 120,
                                    fontSize: '2.5rem'
                                }
                            }}
                            src={`data:image/png;base64,${convertBufferToBase64String()}`}
                        />

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography
                                variant='h4'
                                fontWeight={600}
                                color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}
                                sx={{
                                    ml: 0,
                                    '@media (max-width: 900px)': {
                                        mr: 7,
                                        fontSize: '1.5rem'
                                    }
                                }}
                            >
                                {firstName} {lastName}
                            </Typography>
                            <Typography
                                variant='body1'
                                color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}
                                sx={{
                                    ml: 5,
                                    mr: 5,
                                    mt: 1,
                                    '@media (max-width: 900px)': {
                                        fontSize: '0.875rem',
                                        mr: 10
                                    }
                                }}
                            >
                                {profileType === 'athlete' && 'Athlet'}
                                {profileType === 'coach' && 'Trainer'}
                                {profileType === 'parent' && 'Elternteil'}
                            </Typography>
                            {profileType === 'athlete' && squad && (
                                <Chip
                                    label={squad}
                                    sx={{
                                        mt: 2,
                                        fontWeight: 600,
                                        bgcolor: 'white',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        '@media (max-width: 900px)': {

                                            mt: 1,
                                            mr: 5,
                                            fontSize: '0.8125rem'
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Right Side - Form */}
                    <Box
                        sx={{
                            width: { xs: '100%', md: '65%' },
                            p: 4,
                            overflow: 'auto',
                            maxHeight: { xs: 'calc(90vh - 250px)', md: '90vh' },
                            '@media (max-width: 900px)': {
                                maxHeight: '100%',
                                p: 2,
                                flex: 1,
                                pb: 4,
                                boxSizing: 'border-box'
                            }
                        }}
                    >
                        <Typography
                            variant='h5'
                            fontWeight={600}
                            color={isDarkMode ? darkTheme.textPrimary : undefined}
                            sx={{
                                mb: 3,
                                mt: { xs: 2, md: 0 },
                                '@media (max-width: 900px)': {
                                    fontSize: '1.25rem',
                                    mb: 2,
                                    mt: 1
                                }
                            }}
                        >
                            {profileType === 'athlete' && 'Athlet bearbeiten'}
                            {profileType === 'coach' && 'Trainer bearbeiten'}
                            {profileType === 'parent' && 'Elternteil bearbeiten'}
                        </Typography>

                        <Grid
                            container
                            spacing={3}
                            sx={{
                                '@media (max-width: 900px)': {
                                    margin: '0 !important',
                                    width: '100% !important',
                                    '& > .MuiGrid-item': {
                                        paddingTop: '12px !important',
                                        paddingLeft: '12px !important',
                                        '@media (max-width: 600px)': {
                                            marginRight: '10px',
                                        }
                                    }
                                }
                            }}
                        >
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='Vorname'
                                    variant='outlined'
                                    value={firstName}
                                    onChange={onChangeFirstName}
                                    InputProps={{
                                        startAdornment: (
                                            <PersonOutline sx={{ mr: 1, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        ),
                                    }}
                                    sx={getFieldSx()}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='Nachname'
                                    variant='outlined'
                                    value={lastName}
                                    onChange={onChangeLastName}
                                    InputProps={{
                                        startAdornment: (
                                            <PersonOutline sx={{ mr: 1, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        ),
                                    }}
                                    sx={getFieldSx()}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='Geburtsdatum'
                                    variant='outlined'
                                    value={dateOfBirth}
                                    type='date'
                                    onChange={onChangeDateOfBirth}
                                    InputProps={{
                                        startAdornment: (
                                            <CakeOutlined sx={{ mr: 1, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        ),
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={getFieldSx()}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='E-Mail'
                                    disabled
                                    variant='outlined'
                                    value={email}
                                    InputProps={{
                                        startAdornment: (
                                            <EmailOutlined sx={{ mr: 1, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        ),
                                    }}
                                    sx={getFieldSx(true)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='Telefonnummer'
                                    variant='outlined'
                                    value={telephone}
                                    onChange={(event) => setTelephone(event.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <PhoneOutlined sx={{ mr: 1, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        ),
                                    }}
                                    sx={getFieldSx()}
                                />
                            </Grid>
                            {profileType === 'coach' && (
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={<Checkbox checked={isadmin} onChange={(e) => setIsadmin(e.target.checked)} sx={{ color: isDarkMode ? darkTheme.cardBorder : undefined, '&.Mui-checked': { color: isDarkMode ? darkTheme.accentDark : undefined } }} />}
                                        label="Administrator"
                                        sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}
                                    />
                                </Grid>
                            )}

                            {profileType === 'athlete' && (
                                <Grid item xs={12} sm={6}>
                                    <CustomFormControl sx={{ width: '100%' }} color={color} isDarkMode={isDarkMode} darkTheme={darkTheme}>
                                        <InputLabel
                                            id='squad-label'
                                            sx={{
                                                color: isDarkMode ? darkTheme.textSecondary : undefined,
                                                '&&.Mui-focused': {
                                                    // Override global dark-mode focused label color (higher specificity + !important)
                                                    color: `${color || accentColor} !important`,
                                                },
                                            }}
                                        >
                                            Kader
                                        </InputLabel>
                                        <CustomSelect
                                            ref={setSquadSelectAnchorEl}
                                            id='squad-select'
                                            labelId='squad-label'
                                            value={squad}
                                            label='Kader'
                                            onChange={(event) => setSquad(event.target.value)}
                                            startAdornment={
                                                <InputAdornment position='start'>
                                                    <EmojiEventsOutlined
                                                        sx={{
                                                            color: isDarkMode ? darkTheme.textSecondary : 'action.active',
                                                            ml: 0,
                                                            transition: 'none',
                                                        }}
                                                    />
                                                </InputAdornment>
                                            }
                                            isDarkMode={isDarkMode}
                                            darkTheme={darkTheme}
                                            primaryColor={color}
                                            MenuProps={(() => {
                                                const base = getMenuProps(isDarkMode, darkTheme);
                                                return {
                                                    ...base,
                                                    anchorEl: squadSelectAnchorEl,
                                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                                    PaperProps: {
                                                        ...base.PaperProps,
                                                        sx: {
                                                            ...base.PaperProps?.sx,
                                                            width: squadSelectAnchorEl ? squadSelectAnchorEl.clientWidth : undefined,
                                                            minWidth: squadSelectAnchorEl ? squadSelectAnchorEl.clientWidth : undefined,
                                                        },
                                                    },
                                                };
                                            })()}
                                        >
                                            <CustomMenuItem value='Damen' isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={color}>Damen</CustomMenuItem>
                                            <CustomMenuItem value='Herren' isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={color}>Herren</CustomMenuItem>
                                            <CustomMenuItem value='U19' isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={color}>U19</CustomMenuItem>
                                            <CustomMenuItem value='U17' isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={color}>U17</CustomMenuItem>
                                            <CustomMenuItem value='U15' isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={color}>U15</CustomMenuItem>
                                        </CustomSelect>
                                    </CustomFormControl>
                                </Grid>
                            )}

                            {profileType === 'athlete' && (
                                <>
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                p: 3,
                                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                                borderRadius: 2,
                                                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2
                                            }}
                                        >
                                            <FeedbackOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active', fontSize: 28 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant='subtitle2' sx={{ mb: 1, color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                                    Erweiterte Rückmeldung
                                                </Typography>
                                                <CustomFormControl sx={{ width: '100%' }}>
                                                    <RadioGroup row value={hasExtendedFeedback} onChange={(event) => setHasExtendedFeedback(event.target.value)}>
                                                        <FormControlLabel
                                                            value={true}
                                                            control={
                                                                <Radio sx={{ color: green[500], '&.Mui-checked': { color: green[500] } }} />
                                                            }
                                                            label='Ja'
                                                            sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined, mr: 2 }}
                                                        />
                                                        <FormControlLabel
                                                            value={false}
                                                            control={
                                                                <Radio sx={{ color: red[500], '&.Mui-checked': { color: red[500] } }} />
                                                            }
                                                            label='Nein'
                                                            sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}
                                                        />
                                                    </RadioGroup>
                                                </CustomFormControl>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                                borderRadius: 2,
                                                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <GroupsOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                                <Typography variant='subtitle2' sx={{ color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                                    Trainer
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
                                                {selectedCoaches.map((value, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={`${value.profile.lastName}, ${value.profile.firstName}`}
                                                        sx={{
                                                            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'white',
                                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                            border: isDarkMode ? 'none' : '1px solid rgba(0, 0, 0, 0.08)'
                                                        }}
                                                    />
                                                ))}
                                                {selectedCoaches.length === 0 && (
                                                    <Chip
                                                        label='Keine Trainer zugeordnet'
                                                        variant='outlined'
                                                        sx={{
                                                            color: isDarkMode ? darkTheme.textSecondary : undefined,
                                                            borderColor: isDarkMode ? darkTheme.cardBorder : undefined,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    {selectedParents.length !== 0 && (
                                        <Grid item xs={12} sm={6}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                                    borderRadius: 2,
                                                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                    <GroupsOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                                    <Typography variant='subtitle2' sx={{ color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                                        Eltern
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
                                                    {selectedParents.map((value, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={`${value.profile.lastName}, ${value.profile.firstName}`}
                                                            sx={{
                                                                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'white',
                                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                                border: isDarkMode ? 'none' : '1px solid rgba(0, 0, 0, 0.08)'
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    )}
                                </>
                            )}

                            {(profileType === 'parent' || profileType === 'coach') && (
                                <Grid item xs={12}>
                                    <SearchableAthleteSelector
                                        athletes={menu}
                                        selectedAthletes={selectedAthletes}
                                        onChangeSelectedAthletes={onChangeSelectedAthletes}
                                        label="Athleten"
                                        color={color}
                                        loading={loadingAthletes}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <Divider sx={{
                            mt: 4,
                            mb: 3,
                            '@media (max-width: 900px)': {
                                mt: 3,
                                mb: 2
                            }
                        }} />

                        {/* Action Buttons */}
                        <Stack
                            direction='row'
                            spacing={2}
                            justifyContent='space-between'
                            alignItems='center'
                            sx={{
                                '@media (max-width: 900px)': {
                                    flexDirection: 'column',
                                    gap: 2,
                                    alignItems: 'fit',
                                    width: '97%'
                                }
                            }}
                        >
                            <Button
                                variant='outlined'
                                color='error'
                                size='small'
                                onClick={handleClickForgotPassword}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    fontSize: '0.8rem',
                                    '@media (max-width: 900px)': {
                                        alignItems: 'fit',
                                        width: '100%',
                                        order: 2,
                                        fontSize: '0.75rem',
                                        marginLeft: '10px'
                                    }
                                }}
                            >
                                Passwort zurücksetzen
                            </Button>

                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                '@media (max-width: 900px)': {
                                    order: 1,
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    alignItems: 'center',
                                }
                            }}>
                                <IconButton
                                    onClick={() => setOpenConfirmation(true)}
                                    sx={{
                                        color: red[500],
                                        '&:hover': {
                                            bgcolor: red[50]
                                        },
                                        '@media (max-width: 900px)': {
                                            flexShrink: 0
                                        }
                                    }}
                                >
                                    <DeleteOutlined />
                                </IconButton>

                                <Button
                                    variant='contained'
                                    onClick={editProfile}
                                    sx={{
                                        bgcolor: color,
                                        color: isDarkMode ? darkTheme.textPrimary : 'white',
                                        textTransform: 'none',
                                        px: 4,
                                        borderRadius: 2,
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                        '&:hover': {
                                            bgcolor: color,
                                            filter: 'brightness(0.9)',
                                            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)'
                                        },
                                        '@media (max-width: 900px)': {
                                            mr: '4px',
                                            flex: 1,
                                            px: 3
                                        }
                                    }}
                                >
                                    Aktualisieren
                                </Button>
                            </Box>
                        </Stack>
                    </Box>

                    <Dialog open={openConfirmation} onClose={() => setOpenConfirmation(false)} PaperProps={{ sx: { bgcolor: isDarkMode ? darkTheme.cardBg : 'white' } }}>
                        <DialogTitle sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                            Löschen bestätigen
                        </DialogTitle>

                        <DialogContent>
                            <DialogContentText sx={{ color: isDarkMode ? darkTheme.textSecondary : undefined }}>
                                Soll dieses Profil
                                ({person.profile.lastName},&nbsp;{person.profile.firstName}) gelöscht werden?
                            </DialogContentText>
                        </DialogContent>

                        <DialogActions>
                            <Button sx={{ color: 'red', '&:hover': { background: 'rgba(255, 249, 196, 0.3)' } }} onClick={deleteProfile}>
                                Löschen
                            </Button>

                            <Button sx={{ color: isDarkMode ? darkTheme.textPrimary : 'black', '&:hover': { background: 'rgba(255, 249, 196, 0.3)' } }} onClick={() => setOpenConfirmation(false)}>
                                Abbrechen
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Modal>
        </>
    );
};

export default EditUser;